import { useMemo, useState } from 'react';
import type { Dish, DishType, IngredientCategory, IngredientLine } from '../types';
import { DISH_TYPE_LABELS } from '../types';
import { createDish, updateDish } from '../lib/api';
import { DISH_CATEGORY_ORDER, dishCategories, dishCategory, matchesDishSearch } from '../lib/dish-categories';
import {
  PURCHASE_DISPLAY_CATEGORY_OPTIONS,
  ingredientCategoryFromPurchaseDisplay,
  normalizeIngredientCategory,
  purchaseDisplayCategory,
} from '../lib/ingredient-categories';
import { BookIcon, EditIcon, PlusIcon, SearchIcon, UtensilsIcon, XIcon } from './Icons';

const UNITS = ['斤', '两', '公斤', '克', '只', '个', '包', '袋', '瓶', '桶', '箱', '板', '把', '升', '毫升'];

interface DishDraft {
  id?: string;
  name: string;
  type: DishType;
  category: string;
  standardPeople: number;
  active: boolean;
  ingredients: IngredientLine[];
}

const emptyDraft = (): DishDraft => ({
  name: '',
  type: 'normal',
  category: '热菜',
  standardPeople: 50,
  active: true,
  ingredients: [{ name: '', quantity: '', unit: '斤', ingredientCategory: '其他' }],
});

interface Props {
  dishes: Dish[];
  token: string;
  onChange: (dishes: Dish[]) => void;
  onNotify: (message: string, tone?: 'success' | 'error' | 'info') => void;
}

export function DishLibrary({ dishes, token, onChange, onNotify }: Props) {
  const [draft, setDraft] = useState<DishDraft | null>(null);
  const [filter, setFilter] = useState<'active' | 'inactive'>('active');
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('全部');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const categories = useMemo(() => dishCategories(dishes), [dishes]);
  const visible = useMemo(() => dishes.filter((dish) => {
    const statusMatches = filter === 'active' ? dish.active : !dish.active;
    const categoryMatches = categoryFilter === '全部' || dishCategory(dish) === categoryFilter;
    return statusMatches && categoryMatches && matchesDishSearch(dish, query);
  }), [categoryFilter, dishes, filter, query]);

  const edit = (dish: Dish) => {
    setError('');
    setDraft({
      ...dish,
      category: dishCategory(dish),
      ingredients: dish.ingredients.map((item) => ({
        ...item,
        ingredientCategory: normalizeIngredientCategory(item.ingredientCategory, item.name),
      })),
    });
  };

  const changeDishType = (type: DishType) => {
    if (!draft) return;
    const category = type === 'condiment_restock'
      ? '其他'
      : draft.type === 'condiment_restock' ? '热菜' : draft.category;
    setDraft({ ...draft, type, category });
  };

  const updateIngredient = (index: number, patch: Partial<IngredientLine>) => {
    if (!draft) return;
    setDraft({
      ...draft,
      ingredients: draft.ingredients.map((ingredient, itemIndex) => itemIndex === index ? { ...ingredient, ...patch } : ingredient),
    });
  };

  const removeIngredient = (index: number) => {
    if (!draft || draft.ingredients.length === 1) return;
    setDraft({ ...draft, ingredients: draft.ingredients.filter((_, itemIndex) => itemIndex !== index) });
  };

  const validate = (): string => {
    if (!draft?.name.trim()) return '请输入菜品名称';
    if (!draft.category.trim()) return '请输入菜品分类';
    if (!(DISH_CATEGORY_ORDER as readonly string[]).includes(draft.category)) return '请选择有效的菜品分类';
    if (!draft.ingredients.length) return '至少需要一项食材';
    for (const ingredient of draft.ingredients) {
      if (!ingredient.name.trim()) return '请填写完整的食材名称';
      if (!ingredient.unit.trim()) return '请填写食材单位';
      if (!Number.isFinite(Number(ingredient.quantity)) || Number(ingredient.quantity) <= 0) return '食材用量必须大于 0';
    }
    const keys = draft.ingredients.map((item) => `${item.name.trim()}\u0000${item.unit.trim()}`);
    if (new Set(keys).size !== keys.length) return '同一道菜内存在重复的食材和单位';
    return '';
  };

  const save = async () => {
    const validation = validate();
    if (validation || !draft) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...draft,
        name: draft.name.trim(),
        category: draft.category.trim(),
        ingredients: draft.ingredients.map((item) => ({
          ...(item.id ? { id: item.id } : {}),
          name: item.name.trim(),
          quantity: String(Number(item.quantity)),
          unit: item.unit.trim(),
          ingredientCategory: normalizeIngredientCategory(item.ingredientCategory, item.name),
        })),
      };
      const saved = draft.id
        ? await updateDish(token, payload as Dish)
        : await createDish(token, payload);
      const next = draft.id ? dishes.map((dish) => dish.id === saved.id ? saved : dish) : [...dishes, saved];
      onChange(next);
      onNotify(draft.id ? '菜品配方已更新，历史计划不会改变' : '菜品已加入菜品库', 'success');
      setDraft(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (dish: Dish, active: boolean) => {
    try {
      const saved = await updateDish(token, { ...dish, active });
      onChange(dishes.map((item) => item.id === dish.id ? saved : item));
      onNotify(active ? '菜品已恢复使用' : '菜品已停用，历史记录仍会保留', 'success');
    } catch (reason) {
      onNotify(reason instanceof Error ? reason.message : '操作失败，请重试', 'error');
    }
  };

  if (draft) {
    return (
      <div className="dish-editor">
        <button className="text-button dish-editor__back" type="button" onClick={() => setDraft(null)}>← 返回菜品库</button>
        <div className="form-grid">
          <label className="field field--wide">
            <span>菜品名称</span>
            <input autoFocus value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="例如：番茄炒蛋" />
          </label>
          <label className="field">
            <span>菜品类型</span>
            <select value={draft.type} onChange={(event) => changeDishType(event.target.value as DishType)}>
              <option value="normal">普通菜品</option>
              <option value="condiment_restock">调味品补货</option>
            </select>
          </label>
          <label className="field">
            <span>菜品分类</span>
            <select
              aria-label="菜品分类"
              value={draft.category}
              disabled={draft.type === 'condiment_restock'}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
            >
              {DISH_CATEGORY_ORDER.map((category) => <option value={category} key={category}>{category}</option>)}
            </select>
          </label>
          <label className="field">
            <span>标准人数</span>
            <div className="locked-input"><strong>50</strong><small>人 / 批</small></div>
          </label>
        </div>

        <div className="ingredient-editor__heading">
          <div><h3>固定食材用量</h3><p>用量已包含损耗，后续计划默认沿用。</p></div>
          <button className="button button--soft button--small" type="button" onClick={() => setDraft({ ...draft, ingredients: [...draft.ingredients, { name: '', quantity: '', unit: '斤', ingredientCategory: '其他' }] })}>
            <PlusIcon size={17} /> 添加食材
          </button>
        </div>
        <div className="ingredient-editor">
          <div className="ingredient-editor__labels" aria-hidden="true"><span>食材名称</span><span>类别</span><span>每批用量</span><span>单位</span><span /></div>
          {draft.ingredients.map((ingredient, index) => (
            <div className="ingredient-row" key={ingredient.id ?? index}>
              <input aria-label={`食材 ${index + 1} 名称`} value={ingredient.name} onChange={(event) => updateIngredient(index, { name: event.target.value })} placeholder="食材名称" />
              <select
                aria-label={`食材 ${index + 1} 类别`}
                value={purchaseDisplayCategory(ingredient.ingredientCategory, ingredient.name)}
                onChange={(event) => updateIngredient(index, { ingredientCategory: ingredientCategoryFromPurchaseDisplay(event.target.value as ReturnType<typeof purchaseDisplayCategory>) })}
              >
                {PURCHASE_DISPLAY_CATEGORY_OPTIONS.map((category) => <option value={category} key={category}>{category}</option>)}
              </select>
              <input aria-label={`食材 ${index + 1} 用量`} type="number" inputMode="decimal" min="0.001" step="0.1" value={ingredient.quantity} onChange={(event) => updateIngredient(index, { quantity: event.target.value })} placeholder="0" />
              <input aria-label={`食材 ${index + 1} 单位`} list="ingredient-units" value={ingredient.unit} onChange={(event) => updateIngredient(index, { unit: event.target.value })} placeholder="单位" />
              <button className="icon-button icon-button--danger" type="button" onClick={() => removeIngredient(index)} disabled={draft.ingredients.length === 1} aria-label="移除食材"><XIcon size={18} /></button>
            </div>
          ))}
          <datalist id="ingredient-units">{UNITS.map((unit) => <option value={unit} key={unit} />)}</datalist>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="form-actions">
          <button className="button button--ghost" type="button" onClick={() => setDraft(null)}>取消</button>
          <button className="button button--primary" type="button" disabled={saving} onClick={save}>{saving ? '保存中…' : draft.id ? '保存配方修改' : '添加到菜品库'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dish-library">
      <div className="dish-library__toolbar">
        <div className="segmented" role="group" aria-label="菜品状态">
          <button className={filter === 'active' ? 'is-active' : ''} type="button" onClick={() => setFilter('active')}>使用中 <span>{dishes.filter((dish) => dish.active).length}</span></button>
          <button className={filter === 'inactive' ? 'is-active' : ''} type="button" onClick={() => setFilter('inactive')}>已停用 <span>{dishes.filter((dish) => !dish.active).length}</span></button>
        </div>
        <button className="button button--primary button--small" type="button" onClick={() => setDraft(emptyDraft())}><PlusIcon size={17} /> 新增菜品</button>
      </div>
      <div className="dish-library__filters">
        <label className="dish-library__search">
          <SearchIcon size={17} />
          <input aria-label="搜索菜品库" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜菜品" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="清空菜品库搜索"><XIcon size={15} /></button>}
        </label>
        <label className="dish-library__category">
          <span>分类</span>
          <select aria-label="筛选菜品分类" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
            <option value="全部">全部分类</option>
            {categories.map((category) => <option value={category} key={category}>{category}</option>)}
          </select>
        </label>
        <span className="dish-library__result">显示 {visible.length} 道</span>
      </div>
      {visible.length ? (
        <div className="dish-list">
          {visible.map((dish) => (
            <article className="dish-list-card" key={dish.id}>
              <div className={`dish-list-card__icon ${dish.type === 'condiment_restock' ? 'is-condiment' : ''}`}>
                {dish.type === 'condiment_restock' ? <BookIcon /> : <UtensilsIcon />}
              </div>
              <div className="dish-list-card__content">
                <div className="dish-list-card__title"><h3>{dish.name}</h3><span className="tag">{dishCategory(dish)}</span>{dish.type === 'condiment_restock' && <span className="tag tag--gold">{DISH_TYPE_LABELS[dish.type]}</span>}</div>
                <p>{dish.ingredients.map((item) => `${item.name} ${item.quantity}${item.unit}`).join(' · ')}</p>
                <small>标准 50 人 / 批</small>
              </div>
              <div className="dish-list-card__actions">
                {dish.active && <button className="icon-button" type="button" onClick={() => edit(dish)} aria-label={`编辑${dish.name}`}><EditIcon size={18} /></button>}
                <button className="text-button" type="button" onClick={() => setActive(dish, !dish.active)}>{dish.active ? '停用' : '恢复'}</button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <BookIcon size={32} />
          <h3>{query || categoryFilter !== '全部' ? '没有匹配的菜品' : filter === 'active' ? '还没有可用菜品' : '没有已停用菜品'}</h3>
          <p>{query || categoryFilter !== '全部' ? '换个关键词或分类试试。' : filter === 'active' ? '添加第一道菜，即可开始安排每周菜单。' : '停用的菜品会保留在这里。'}</p>
        </div>
      )}
    </div>
  );
}
